// src/components/maps/AdvancedMarkersMap.jsx
import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import { GoogleMap, DirectionsRenderer } from "@react-google-maps/api";
import { GOOGLE_MAPS_MAP_ID } from "../config/maps";

const DEFAULT_CONTAINER_STYLE = { width: "100%", height: "400px" };

function AdvancedMarkersMap({
  // container
  mapContainerClassName,
  containerStyle = DEFAULT_CONTAINER_STYLE,

  // viewport fallback (only used until we successfully fit)
  center = { lat: 39.5, lng: -98.35 },
  zoom = 4,

  // markers
  properties = [],
  markerPosition, // single-marker mode
  onMarkerClick,

  // directions
  directions,
  showDirections = false,

  // points to fit (array of {lat,lng})
  fitPoints,
}) {
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const onMarkerClickRef = useRef(onMarkerClick);

  const [mapReady, setMapReady] = useState(false);

  // viewport control
  const hasFitRef = useRef(false); // "we have successfully fit bounds at least once"
  const fitRequestIdRef = useRef(0); // increments whenever we want to re-fit (data changed)

  // keep callback ref fresh without retriggering marker effect
  useEffect(() => {
    onMarkerClickRef.current = onMarkerClick;
  }, [onMarkerClick]);

  const logViewport = useCallback((tag) => {
    const map = mapRef.current;
    if (!map) return;
    try {
      map.getCenter?.();
    } catch (e) {
      console.log(`[Map] ${tag} (log failed)`, e);
    }
  }, []);

  const clearMarkers = useCallback(() => {
    markersRef.current.forEach((m) => {
      if (!m) return;
      if ("map" in m) m.map = null;
      else if (typeof m.setMap === "function") m.setMap(null);
    });
    markersRef.current = [];
  }, []);

  const onLoad = useCallback(
    (map) => {
      mapRef.current = map;
      setMapReady(true);

      logViewport("after onLoad");

      // Helpful to see if something else overrides later
      const idleListener = window.google?.maps?.event?.addListener(map, "idle", () => {
        logViewport("on idle");
      });

      // cleanup if map unmounts
      return () => {
        if (idleListener) window.google.maps.event.removeListener(idleListener);
      };
    },
    [logViewport]
  );

  /**
   * Decide when we SHOULD attempt a fit.
   * We treat "fitPoints available" + (list mode markers or single marker) as a request to fit.
   */
  useEffect(() => {
    if (!mapReady) return;

    const hasList = Array.isArray(properties) && properties.length > 0;
    const hasSingle =
      markerPosition?.lat != null &&
      markerPosition?.lng != null &&
      Number.isFinite(Number(markerPosition.lat)) &&
      Number.isFinite(Number(markerPosition.lng));

    const pointsOk = Array.isArray(fitPoints) && fitPoints.length >= 1;

    // only request a fit when we have something meaningful
    if (!pointsOk && !hasSingle) return;
    if (!hasList && !hasSingle) return;

    fitRequestIdRef.current += 1;
  }, [mapReady, properties, markerPosition, fitPoints]);

  /**
   * Create markers (list mode or single mode).
   * This effect is intentionally NOT dependent on onMarkerClick identity.
   */
  useEffect(() => {
    if (!mapReady) return;
    if (!window.google?.maps) {
      console.warn("[Markers] window.google.maps not ready yet");
      return;
    }
    const map = mapRef.current;
    if (!map) return;

    const hasAdvanced = !!window.google.maps.marker?.AdvancedMarkerElement;

    clearMarkers();

    // ---- Single-marker mode ----
    if (markerPosition?.lat != null && markerPosition?.lng != null) {
      const lat = Number(markerPosition.lat);
      const lng = Number(markerPosition.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        console.warn("[Markers] invalid single marker coords", markerPosition);
        return;
      }

      try {
        const marker = hasAdvanced
          ? new window.google.maps.marker.AdvancedMarkerElement({
              map,
              position: { lat, lng },
              title: "Location",
            })
          : new window.google.maps.Marker({
              map,
              position: { lat, lng },
              title: "Location",
            });

        markersRef.current.push(marker);
        logViewport("after single marker create");
      } catch (err) {
        console.error("[Markers] error creating single marker", err);
      }

      return; // do NOT create list markers
    }

    // ---- List mode ----

    properties.forEach((p, idx) => {
      const latRaw = p?.latitude ?? p?.coordinates?.lat;
      const lngRaw = p?.longitude ?? p?.coordinates?.lng;
      const lat = Number(latRaw);
      const lng = Number(lngRaw);

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        console.warn(`[Markers] skip idx=${idx} invalid coords`, { latRaw, lngRaw, p });
        return;
      }

      const pos = { lat, lng };
      const title = p?.address1 || String(p?.propertyId || "Property");

      try {
        const marker = hasAdvanced
          ? new window.google.maps.marker.AdvancedMarkerElement({
              map,
              position: pos,
              title,
            })
          : new window.google.maps.Marker({
              map,
              position: pos,
              title,
            });

        const clickCb = onMarkerClickRef.current;
        if (clickCb) {
          const evt = hasAdvanced ? "gmp-click" : "click";
          marker.addListener(evt, () => clickCb(p));
        }

        markersRef.current.push(marker);
      } catch (err) {
        console.error("[Markers] error creating marker", err, p);
      }
    });

    logViewport("after list markers create");

    return () => clearMarkers();
  }, [mapReady, properties, markerPosition, clearMarkers, logViewport]);

  /**
   * Fit bounds. This runs after a fit is "requested".
   * Key points:
   * - wait for map to be ready
   * - wait for at least one marker scenario
   * - run on next idle ONCE, but also handle the case where idle already happened
   * - after successful fit, stop passing center/zoom (prevents override snap-back)
   */
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;

    const requestId = fitRequestIdRef.current;
    if (requestId === 0) return;

    const hasList = Array.isArray(properties) && properties.length > 0;
    const hasSingle =
      markerPosition?.lat != null &&
      markerPosition?.lng != null &&
      Number.isFinite(Number(markerPosition.lat)) &&
      Number.isFinite(Number(markerPosition.lng));

    if (!hasList && !hasSingle) return;

    const points =
      Array.isArray(fitPoints) && fitPoints.length > 0
        ? fitPoints
        : hasSingle
        ? [{ lat: Number(markerPosition.lat), lng: Number(markerPosition.lng) }]
        : null;

    if (!points || points.length === 0) return;

    const fit = () => {
      // ignore stale requests
      if (requestId !== fitRequestIdRef.current) return;

      try {
        const b = new window.google.maps.LatLngBounds();
        points.forEach((p) => b.extend(p));
        map.fitBounds(b);

        hasFitRef.current = true;
        logViewport("after fitBounds");
      } catch (e) {
        console.warn("[Fit] failed", e);
      }
    };

    // If map already has bounds (often true), fit immediately
    if (map.getBounds?.()) {
      fit();
      return;
    }

    const listener = window.google.maps.event.addListenerOnce(map, "idle", fit);
    return () => window.google.maps.event.removeListener(listener);
  }, [mapReady, properties, markerPosition, fitPoints, logViewport]);

  // IMPORTANT: only supply center/zoom until first successful fit
  const viewportProps = !hasFitRef.current ? { center, zoom } : {};

  return (
    <div role="region" aria-label="Map showing property locations" style={{ width: "100%", height: "100%" }}>
      <GoogleMap
        mapContainerStyle={mapContainerClassName ? undefined : containerStyle}
        mapContainerClassName={mapContainerClassName}
        onLoad={onLoad}
        options={{ mapId: GOOGLE_MAPS_MAP_ID }}
        {...viewportProps}
      >
        {showDirections && directions && <DirectionsRenderer directions={directions} />}
      </GoogleMap>
    </div>
  );
}

export default memo(AdvancedMarkersMap);
